/**
 * KDF: MASTER KEY derivation from the master password.
 *
 * Algorithm: **Argon2id** (winner of Password Hashing Competition; resists
 * GPU/ASIC attacks due to memory cost and side-channel attacks due to its
 * design). WebCrypto does not support it, so we use `@noble/hashes` (pure JS
 * audited, without native binaries → runs in browser, Bun, and Node).
 *
 * Parameters (OWASP Password Storage Cheat Sheet, interactive profile):
 *   - m = 19 MiB (19456 KiB), t = 2, p = 1   → "INTERACTIVE" (mobile/unlock)
 *   - m = 64 MiB (65536 KiB), t = 3, p = 4   → "SENSITIVE"   (account creation)
 * The `salt` is a random 16-byte value per user stored next to the blob
 * (public: not secret). Output length is 32 bytes (AES-256).
 *
 * ZERO-KNOWLEDGE INVARIANT: the master key produced here is NEVER
 * serialized to the server. What the server receives is (a) the `authHash`
 * (one-way verifier, separate domain) and (b) encrypted blobs. See
 * `account.ts` for wrap-key / auth-hash separation.
 */

import { argon2id } from "@noble/hashes/argon2.js";
import { hkdfExpand } from "./primitives";

export const MASTER_KEY_BYTES = 32;
export const KDF_SALT_BYTES = 16;

export interface Argon2Params {
  /** Memory cost in KiB. */
  readonly m: number;
  /** Iteraciones (time cost). */
  readonly t: number;
  /** Paralelismo (lanes). */
  readonly p: number;
}

export const KDF_PRESETS = {
  /** Desbloqueo frecuente / dispositivos modestos. */
  interactive: { m: 19_456, t: 2, p: 1 },
  /** Account creation / high-value operations. */
  sensitive: { m: 65_536, t: 3, p: 4 },
} as const satisfies Record<string, Argon2Params>;

export type KdfPresetName = keyof typeof KDF_PRESETS;

export interface KdfConfig {
  readonly algorithm: "argon2id";
  readonly params: Argon2Params;
  /** salt in base64 (public). */
  readonly salt: string;
  /** Schema version, for future migrations. */
  readonly v: 1;
}

export function newKdfSaltBytes(): Uint8Array {
  const s = new Uint8Array(KDF_SALT_BYTES);
  crypto.getRandomValues(s);
  return s;
}

/**
 * Derives master key (32 bytes) from password + salt with Argon2id.
 * Pure and deterministic function for identical (password, salt, params).
 */
export function deriveMasterKey(
  password: string,
  salt: Uint8Array,
  params: Argon2Params = KDF_PRESETS.interactive,
): Uint8Array {
  if (salt.length < KDF_SALT_BYTES) {
    throw new Error("salt too short");
  }
  return argon2id(new TextEncoder().encode(password.normalize("NFKC")), salt, {
    m: params.m,
    t: params.t,
    p: params.p,
    dkLen: MASTER_KEY_BYTES,
  });
}

/**
 * Wrap key: master sub-key dedicated to wrapping
 * the account key. Separate domain from auth-hash via distinct HKDF-info.
 */
export async function deriveMasterWrapKey(masterKey: Uint8Array): Promise<Uint8Array> {
  return hkdfExpand(masterKey, "boveda:e2e-crypto:v1:master-wrap", MASTER_KEY_BYTES);
}

/**
 * Authentication verifier for the server. Derived from the master key
 * with a distinct HKDF `info` from the wrap key, such that:
 *   - the server can verify the password (comparing its hash of
 *     authHash) WITHOUT learning master key or wrap key;
 *   - leaking authHash DOES NOT reveal wrap key (domain separation).
 * The server also re-hashes authHash with its own salt before
 * storing it (never stores raw authHash).
 */
export async function deriveAuthHash(masterKey: Uint8Array): Promise<Uint8Array> {
  return hkdfExpand(masterKey, "boveda:e2e-crypto:v1:auth-verifier", MASTER_KEY_BYTES);
}
